/** Save archive overlay: list slots, export/import/duplicate/delete. */

import type { EmFS } from '../engine';
import {
  deleteSlot,
  downloadBlob,
  duplicateSlot,
  exportSlot,
  importSlotZip,
  listSlots,
  queueSaveMutation,
  restoreSlotSnapshot,
  snapshotSlot,
  slotCount,
  type SlotInfo,
} from '../saves';
import { createZip } from '../zip/write';
import { deleteRestored, listRestored, type RestoredSave } from '../store';
import { $, bytes, confirmDanger, toast } from './dom';
import { mountBackupControls } from './backup';
import { mountStorageHealth } from './storage-health';

export interface SaveArchive {
  /** `edit` puts the editor first on every row and says so at the top. */
  open(mode?: ArchiveMode): void;
  close(): void;
  toggle(mode?: ArchiveMode): void;
  refresh(): void;
  readonly isOpen: boolean;
}

/**
 * Which job the archive is open for.
 *
 * The editor is not a separate destination — it acts on a slot, and the slot
 * list is where slots are chosen. Rather than duplicate that list, the HUD's
 * "Save editor" button opens this same overlay in `edit` mode: same rows, same
 * actions, but Edit leads and is styled as the primary one. `manage` is the
 * archive as it always was.
 */
export type ArchiveMode = 'manage' | 'edit';

const fmtDate = (d: Date | null) =>
  d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtDateTime = (d: Date | null) =>
  d ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function describe(info: SlotInfo): { title: string; sub: string } {
  if (!info.occupied) return { title: '— empty —', sub: '' };
  if (!info.header)
    return {
      title: 'unreadable save',
      sub: `${info.error ?? 'SAVE.DAT could not be parsed'} · ${info.fileCount} files · ${bytes(info.bytes)}`,
    };
  const h = info.header;
  const bits = [
    h.mapFile ? h.mapFile.replace(/\.SAV$/i, '') : null,
    `saved ${fmtDateTime(info.mtime) !== '—' ? fmtDateTime(info.mtime) : fmtDate(h.savedAt)}`,
    `${info.fileCount} files · ${bytes(info.bytes)}`,
  ].filter(Boolean);
  return { title: h.description || '(no description)', sub: bits.join(' · ') };
}

export function mountSaveArchive(
  getFS: () => EmFS | null,
  onMutate: () => Promise<void>,
  onEdit: (slot: number) => void,
  canMutate: () => boolean = () => true,
): SaveArchive {
  const overlay = $('#overlay-saves');
  const list = $('#slot-list');
  const zipInput = $<HTMLInputElement>('#input-slot-zip');
  const heading = $('#saves-title');
  const lede = $('#saves-lede');
  let importTarget = 1;
  let mode: ArchiveMode = 'manage';
  const health = mountStorageHealth();
  const backup = mountBackupControls(health.footer, () => health.refresh());

  /**
   * Saves lifted out of a restored backup, listed above the footer.
   *
   * They are not in a slot yet and that is the point: a restore must never
   * overwrite a live game, so the file's contents wait here until the player
   * says which slot they belong in.
   */
  const restoredSection = document.createElement('div');
  restoredSection.id = 'rr-restored';
  restoredSection.hidden = true;
  health.footer.before(restoredSection);

  const mutationAllowed = (): boolean => {
    if (canMutate()) return true;
    toast('Save changes are disabled while a game is running. Return to the main menu first.', 'bad', 9000);
    return false;
  };

  const commitSlotMutation = async <T>(slot: number, operation: () => Promise<T> | T): Promise<T> => {
    return queueSaveMutation(async () => {
      const before = snapshotSlot(getFS()!, slot);
      try {
        const result = await operation();
        await onMutate();
        return result;
      } catch (error) {
        try {
          restoreSlotSnapshot(getFS()!, slot, before);
          try { await onMutate(); } catch { /* preserve the original failure */ }
        } catch (rollbackError) {
          throw new Error(
            `${(error as Error).message}; rollback of slot ${slot} also failed: ${(rollbackError as Error).message}`,
          );
        }
        throw new Error(`${(error as Error).message}; slot ${slot} was rolled back`);
      }
    });
  };

  const render = () => {
    const FS = getFS();
    health.refresh();
    backup.refresh();
    renderRestored();
    list.replaceChildren();
    heading.textContent = mode === 'edit' ? 'Save editor' : 'Save archive';
    lede.textContent =
      mode === 'edit'
        ? 'Pick the slot to edit. Everything else the archive does is still here on each row.'
        : '';
    lede.hidden = mode !== 'edit';
    if (!FS) {
      const p = document.createElement('p');
      p.className = 'hint';
      p.textContent = 'The engine has not booted yet, so the save filesystem is not mounted. Start the game first.';
      list.appendChild(p);
      return;
    }
    const slots = listSlots(FS);
    for (const info of slots) {
      const { title, sub } = describe(info);
      const row = document.createElement('div');
      row.className = `slot${info.occupied ? '' : ' empty'}`;

      const n = document.createElement('div');
      n.className = 'n';
      n.textContent = `SLOT ${String(info.slot).padStart(2, '0')}`
        + (info.role === 'manual' ? '' : ` · ${info.role}`);

      const mid = document.createElement('div');
      if (info.header?.characterName) {
        const who = document.createElement('div');
        who.className = 'who';
        who.textContent = info.header.characterName;
        mid.appendChild(who);
      }
      const desc = document.createElement('div');
      desc.className = 'desc';
      desc.textContent = title;
      mid.appendChild(desc);
      if (sub) {
        const meta = document.createElement('div');
        meta.className = 'meta';
        meta.textContent = sub;
        mid.appendChild(meta);
      }

      const acts = document.createElement('div');
      acts.className = 'acts';
      const mk = (label: string, cls: string, fn: () => void) => {
        const b = document.createElement('button');
        b.className = `tiny ${cls}`;
        b.textContent = label;
        b.addEventListener('click', fn);
        acts.appendChild(b);
        return b;
      };

      if (info.occupied) {
        // Only offered where the header already parsed: an unreadable SAVE.DAT
        // has nothing for the editor to open, and the row says so already.
        //
        // Styled `primary` rather than `ghost` in both modes. Five identical
        // links is a list of five equally-likely things, and the editor is the
        // one people are looking for; Export/Duplicate/Delete stay ghosts.
        if (info.header) mk('Edit', 'primary', () => {
          if (mutationAllowed()) onEdit(info.slot);
        });
        mk('Export', 'ghost', async () => {
          try {
            const { blob, filename } = await queueSaveMutation(() => exportSlot(FS, info.slot));
            downloadBlob(blob, filename);
            toast(`Exported slot ${info.slot} (${bytes(blob.size)})`);
          } catch (e) {
            toast(`Export failed: ${(e as Error).message}`, 'bad');
          }
        });
        mk('Duplicate', 'ghost', async () => {
          if (!mutationAllowed()) return;
          const target = await chooseTarget(`a copy of slot ${info.slot}`, info.slot);
          if (!target) return;
          try {
            await commitSlotMutation(target, () => duplicateSlot(FS, info.slot, target));
            render();
            toast(`Copied slot ${info.slot} → slot ${target}`);
          } catch (e) {
            toast(`Duplicate failed: ${(e as Error).message}`, 'bad');
          }
        });
        mk('Delete', 'danger', async () => {
          if (!mutationAllowed()) return;
          const label = info.header?.description || `slot ${info.slot}`;
          if (!(await confirmDanger(
            `Delete "${label}"?${info.role === 'manual' ? '' : ` This is an engine-managed ${info.role} slot.`} `
              + 'This cannot be undone — export it first if you might want it back.'
          )))
            return;
          try {
            await commitSlotMutation(info.slot, () => deleteSlot(FS, info.slot));
            render();
            toast(`Deleted slot ${info.slot}`, 'warn');
          } catch (e) {
            toast(`Delete failed: ${(e as Error).message}`, 'bad');
          }
        });
      }
      mk(info.occupied ? 'Replace…' : 'Import…', 'ghost', async () => {
        if (!mutationAllowed()) return;
        if (info.occupied && !(await confirmDanger(
          `Replace slot ${info.slot}${info.role === 'manual' ? '' : ` (${info.role})`}? `
            + 'The archive is validated completely before the existing slot is swapped.'
        ))) return;
        importTarget = info.slot;
        zipInput.value = '';
        zipInput.click();
      });

      row.append(n, mid, acts);
      list.appendChild(row);
    }
  };

  /** The slot a promotion should land in: the first free manual one, or an ask. */
  const chooseTarget = async (from: string, exclude?: number): Promise<number | null> => {
    const FS = getFS();
    if (!FS) return null;
    const slots = listSlots(FS);
    const free = slots.find((s) => !s.occupied && s.role === 'manual' && s.slot !== exclude);
    const count = slotCount();
    const target = free ? free.slot : Number(prompt(`All manual slots are full. Overwrite which slot (1-${count})?`, ''));
    if (!target || target < 1 || target > count || target === exclude) {
      // window.prompt is suppressed in the embedded pane (returns null with no
      // dialog), so the full-slots case must say something rather than no-op.
      if (!free) toast('All manual slots are full — export or delete one first, then retry.', 'warn', 7000);
      return null;
    }
    const targetInfo = slots[target - 1];
    if (
      (!free || targetInfo?.role !== 'manual') &&
      !(await confirmDanger(
        `Slot ${target}${targetInfo?.role !== 'manual' ? ` is managed as a ${targetInfo?.role}` : ''} `
          + `and will be overwritten by ${from}. Continue?`
      ))
    )
      return null;
    return target;
  };

  const describeRestored = (save: RestoredSave): string =>
    [
      save.mapFile ? save.mapFile.replace(/\.SAV$/i, '') : null,
      save.savedAt ? `saved ${fmtDate(new Date(save.savedAt))}` : null,
      `${save.files.length} files · ${bytes(save.bytes)}`,
      save.origin,
    ]
      .filter(Boolean)
      .join(' · ');

  const renderRestored = () => {
    void listRestored()
      .then(async (saves) => {
        restoredSection.replaceChildren();
        restoredSection.hidden = saves.length === 0;
        if (!saves.length) return;

        const heading = document.createElement('h3');
        heading.textContent = `Restored from backup (${saves.length})`;
        const lede = document.createElement('p');
        lede.className = 'hint';
        lede.textContent =
          'These came out of a backup file and are not in a slot yet. Nothing you are playing has been touched.';
        restoredSection.append(heading, lede);

        const list = document.createElement('div');
        list.className = 'slots';
        for (const save of saves) {
          const row = document.createElement('div');
          row.className = 'slot';

          const n = document.createElement('div');
          n.className = 'n';
          n.textContent = 'RESTORED';

          const mid = document.createElement('div');
          if (save.characterName) {
            const who = document.createElement('div');
            who.className = 'who';
            who.textContent = save.characterName;
            mid.appendChild(who);
          }
          const desc = document.createElement('div');
          desc.className = 'desc';
          desc.textContent = save.description || '(no description)';
          const meta = document.createElement('div');
          meta.className = 'meta';
          meta.textContent = describeRestored(save);
          mid.append(desc, meta);

          const acts = document.createElement('div');
          acts.className = 'acts';
          const mk = (label: string, cls: string, fn: () => void) => {
            const b = document.createElement('button');
            b.className = `tiny ${cls}`;
            b.textContent = label;
            b.addEventListener('click', fn);
            acts.appendChild(b);
          };

          mk('Put in a slot…', 'primary', async () => {
            if (!mutationAllowed()) return;
            const FS = getFS();
            if (!FS) {
              toast('Start the game first — the save filesystem is not mounted yet.', 'warn');
              return;
            }
            const target = await chooseTarget(save.description || save.characterName || 'a restored save');
            if (!target) return;
            try {
              const files = await Promise.all(
                save.files.map(async (file) => ({
                  path: file.path,
                  bytes: new Uint8Array(await file.blob.arrayBuffer()),
                }))
              );
              await commitSlotMutation(target, () => restoreSlotSnapshot(FS, target, files));
              await deleteRestored(save.id);
              render();
              toast(`Restored into slot ${target}.`);
            } catch (e) {
              toast(`Restore failed: ${(e as Error).message}`, 'bad');
            }
          });
          mk('Export', 'ghost', async () => {
            try {
              const inputs = await Promise.all(
                save.files.map(async (file) => ({
                  name: file.path,
                  data: new Uint8Array(await file.blob.arrayBuffer()),
                  lastModified: save.savedAt ?? save.restoredAt,
                }))
              );
              downloadBlob(await createZip(inputs), `restored-${save.id}.zip`);
            } catch (e) {
              toast(`Export failed: ${(e as Error).message}`, 'bad');
            }
          });
          mk('Discard', 'danger', async () => {
            if (!(await confirmDanger('Discard this restored save? The backup file it came from still has it.'))) return;
            await deleteRestored(save.id);
            renderRestored();
          });

          row.append(n, mid, acts);
          list.appendChild(row);
        }
        restoredSection.appendChild(list);
      })
      .catch((error) => {
        console.warn('[restore] could not list restored saves', error);
      });
  };

  zipInput.addEventListener('change', async () => {
    const FS = getFS();
    const file = zipInput.files?.[0];
    if (!FS || !file) return;
    try {
      const n = await commitSlotMutation(importTarget, () => importSlotZip(FS, importTarget, file));
      render();
      toast(`Imported ${n} files into slot ${importTarget}`);
    } catch (e) {
      toast(`Import failed: ${(e as Error).message}`, 'bad');
    }
  });

  const api: SaveArchive = {
    get isOpen() {
      return overlay.classList.contains('open');
    },
    open(next: ArchiveMode = 'manage') {
      mode = next;
      render();
      overlay.classList.add('open');
    },
    close() {
      overlay.classList.remove('open');
    },
    // Re-opening in a different mode is not a close: pressing "Save editor"
    // while the archive is already up should switch it, not dismiss it.
    toggle(next: ArchiveMode = 'manage') {
      if (api.isOpen && mode === next) api.close();
      else api.open(next);
    },
    refresh() {
      if (api.isOpen) render();
    },
  };

  $('#btn-saves-close').addEventListener('click', () => api.close());
  $('#btn-saves-refresh').addEventListener('click', () => render());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) api.close();
  });

  return api;
}
